import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useNdPrompt, useUpdateNdPrompt } from "@/hooks/use-nd-prompt";

export function NdPromptEditor() {
  const { data, isLoading, isError, error, refetch } = useNdPrompt();
  const update = useUpdateNdPrompt();
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (data) setDraft(data.value);
  }, [data]);

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load prompt"} onRetry={() => refetch()} />
    );
  }

  if (isLoading || !data) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const dirty = draft !== data.value;

  const onSave = () => {
    update.mutate(draft);
  };

  return (
    <Card className="border-border/50">
      <CardContent className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">ND prompt</h2>
          <p className="text-sm text-muted-foreground mt-1">
            The prompt users copy from the &quot;Copy prompt&quot; button. Paste-ready instructions
            for Claude or Cursor to produce an architecture markdown file.
            {data.isDefault && (
              <span className="ml-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs">
                Default
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Use <code className="font-mono bg-muted px-1 rounded">{"{{solution_name}}"}</code> as a
            placeholder. When a user copies the prompt from a specific solution, it&apos;s replaced
            with that solution&apos;s name; otherwise it becomes &quot;this solution&quot;.
          </p>
        </div>

        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={24}
          className="font-mono text-xs"
          spellCheck={false}
        />

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {draft.length.toLocaleString()} characters
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDraft(data.value)}
              disabled={!dirty || update.isPending}
            >
              Discard changes
            </Button>
            <Button
              type="button"
              onClick={onSave}
              disabled={!dirty || update.isPending || !draft.trim()}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
